import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Building2, ExternalLink, MapPin } from 'lucide-react';

const ELECTRONIC_OFFICES = [
  { name: 'Gobierno de Canarias', url: 'https://sede.gobiernodecanarias.org/sede/', type: 'regional' },
  { name: 'Cabildo Insular de Tenerife', url: 'https://sede.tenerife.es/', type: 'insular' },
  { name: 'Ayuntamiento de Adeje', url: 'https://www.ayuntamientodeadeje.es/Siac/', type: 'municipal' },
  { name: 'Ayuntamiento de Arafo', url: 'https://elpinardeelhierro.sedelectronica.es', type: 'municipal' },
  { name: 'Ayuntamiento de Arico', url: 'https://ayuntamientodearico.sedelectronica.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Arona', url: 'https://www.arona.org/Tramites/', type: 'municipal' },
  { name: 'Ayuntamiento de Buenavista del Norte', url: 'https://buenavistadelnorte.sedelectronica.es', type: 'municipal' },
  { name: 'Ayuntamiento de Candelaria', url: 'https://sedeelectronica.candelaria.es', type: 'municipal' },
  { name: 'Ayuntamiento de El Rosario', url: 'https://elrosario.sedelectronica.es', type: 'municipal' },
  { name: 'Ayuntamiento de El Sauzal', url: 'https://elsauzal.sedelectronica.es/info.0', type: 'municipal' },
  { name: 'Ayuntamiento de El Tanque', url: 'https://eltanque.sedelectronica.es', type: 'municipal' },
  { name: 'Ayuntamiento de Fasnia', url: 'https://ayuntamientodefasnia.sedelectronica.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Garachico', url: 'https://eadmin.garachico.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Granadilla de Abona', url: 'https://sede.granadilladeabona.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Guía de Isora', url: 'https://servicios.guiadeisora.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Güímar', url: 'https://guimar.sedelectronica.es', type: 'municipal' },
  { name: 'Ayuntamiento de Icod de los Vinos', url: 'https://sede.icoddelosvinos.es/', type: 'municipal' },
  { name: 'Ayuntamiento de La Guancha', url: 'https://laguancha.sedelectronica.es', type: 'municipal' },
  { name: 'Ayuntamiento de La Matanza de Acentejo', url: 'https://sede.matanceros.es/', type: 'municipal' },
  { name: 'Ayuntamiento de La Orotava', url: 'https://laorotava.sedelectronica.es', type: 'municipal' },
  { name: 'Ayuntamiento de La Victoria de Acentejo', url: 'https://sede.lavictoriadeacentejo.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Los Realejos', url: 'https://sede.losrealejos.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Los Silos', url: 'https://lossilos.sedelectronica.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Puerto de la Cruz', url: 'https://puertodelacruz.sedelectronica.es/', type: 'municipal' },
  { name: 'Ayuntamiento de San Cristóbal de La Laguna', url: 'https://sede.aytolalaguna.es/', type: 'municipal' },
  { name: 'Ayuntamiento de San Juan de la Rambla', url: 'https://sede.sanjuandelarambla.es', type: 'municipal' },
  { name: 'Ayuntamiento de San Miguel de Abona', url: 'https://aytosanmigueldeabona.sedelectronica.es/info.0', type: 'municipal' },
  { name: 'Ayuntamiento de Santa Cruz de Tenerife', url: 'https://sede.santacruzdetenerife.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Santa Úrsula', url: 'https://sede.santaursula.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Santiago del Teide', url: 'https://sede.santiagodelteide.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Tacoronte', url: 'https://sede.tacoronte.es/', type: 'municipal' },
  { name: 'Ayuntamiento de Tegueste', url: 'https://sede.tegueste.es', type: 'municipal' },
  { name: 'Ayuntamiento de Vilaflor de Chasna', url: 'https://vilaflordechasna.sedelectronica.es', type: 'municipal' },
];

export function PublicServicesPage() {
  const { language } = useLanguage();

  const t = {
    title: language === 'es' ? 'Trámites y Servicios Públicos' : 'Procedures & Public Services',
    subtitle: language === 'es' ? 'Sedes electrónicas' : 'Electronic offices',
    description: language === 'es'
      ? 'Accede rápidamente a las sedes electrónicas de los ayuntamientos de Tenerife, el Cabildo Insular y el Gobierno de Canarias. Desde aquí podrás realizar gestiones como empadronamiento, solicitudes, pagos de tasas, certificados y otros trámites administrativos sin desplazarte.'
      : 'Quickly access the electronic offices of Tenerife municipalities, the Island Council and the Government of the Canary Islands. From here you can carry out procedures such as registration, applications, fee payments, certificates and other administrative procedures without traveling.',
    regional: language === 'es' ? 'Gobierno Autonómico' : 'Regional Government',
    insular: language === 'es' ? 'Cabildo Insular' : 'Island Council',
    municipal: language === 'es' ? 'Ayuntamientos' : 'Municipalities',
    accessSite: language === 'es' ? 'Acceder a la sede' : 'Access office',
    totalOffices: language === 'es' ? 'sedes electrónicas disponibles' : 'electronic offices available',
  };

  const regionalOffices = ELECTRONIC_OFFICES.filter(o => o.type === 'regional');
  const insularOffices = ELECTRONIC_OFFICES.filter(o => o.type === 'insular');
  const municipalOffices = ELECTRONIC_OFFICES.filter(o => o.type === 'municipal');

  const OfficeCard = ({ office }) => (
    <a
      href={office.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-4 bg-white border border-slate-200 rounded-xl hover:border-[#00599c] hover:shadow-md transition-all duration-200 group"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-[#00599c]/10 flex items-center justify-center transition-colors">
            <MapPin className="w-5 h-5 text-slate-500 group-hover:text-[#00599c]" />
          </div>
          <span className="font-medium text-[#252a34] group-hover:text-[#00599c] transition-colors">
            {office.name}
          </span>
        </div>
        <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#00599c] transition-colors" />
      </div>
    </a>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-5 h-5 text-[#00599c]" />
          <span className="text-sm font-medium text-[#00599c] uppercase tracking-wider">
            {t.subtitle}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-4">
          {t.title}
        </h1>
        <p className="text-[#252a34]/70 leading-relaxed">
          {t.description}
        </p>
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[#00599c]/10 rounded-full">
          <span className="text-sm font-medium text-[#00599c]">
            {ELECTRONIC_OFFICES.length} {t.totalOffices}
          </span>
        </div>
      </div>

      {/* Regional Government */}
      <Card className="mb-6 border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#00599c] to-[#0077cc] p-4">
          <h2 className="text-lg font-bold text-white font-['Outfit'] flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t.regional}
          </h2>
        </div>
        <CardContent className="p-4">
          <div className="grid gap-3">
            {regionalOffices.map((office, idx) => (
              <OfficeCard key={idx} office={office} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Insular Government */}
      <Card className="mb-6 border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#16a34a] to-[#22c55e] p-4">
          <h2 className="text-lg font-bold text-white font-['Outfit'] flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t.insular}
          </h2>
        </div>
        <CardContent className="p-4">
          <div className="grid gap-3">
            {insularOffices.map((office, idx) => (
              <OfficeCard key={idx} office={office} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Municipalities */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[#f97316] to-[#fb923c] p-4">
          <h2 className="text-lg font-bold text-white font-['Outfit'] flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t.municipal}
          </h2>
          <p className="text-white/80 text-sm mt-1">
            {municipalOffices.length} {language === 'es' ? 'ayuntamientos' : 'municipalities'}
          </p>
        </div>
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {municipalOffices.map((office, idx) => (
              <OfficeCard key={idx} office={office} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
